import { Component, lazy, Suspense, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Move3d } from "lucide-react";
import { EASE, SectionHead } from "@/components/motion/Reveal";
import { useMachines } from "@/components/machines/MachineProvider";
import { MACHINES, IMG } from "@/data/site";
import { cn } from "@/lib/utils";

const Showroom3D = lazy(() => import("@/components/three/Showroom3D"));

class Boundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <img src={IMG.clawPair} alt="" className="h-full w-full object-cover opacity-60" /> : this.props.children;
  }
}

const ITEMS = [
  { name: "Premium Claw Crane", tag: "Claw & Prize", desc: "Lit skill crane with a live-moving claw, stocked prizes and a bright marquee that pulls guests across the room.", machine: "premium-claw" },
  { name: "Commercial Jukebox", tag: "Jukeboxes", desc: "A glowing centerpiece that lets guests control the music — and pay to play it.", machine: "jukebox" },
  { name: "Coin-Op Pool Table", tag: "Pool Tables", desc: "Slate-bed, league-ready coin-op table built for bar floors and busy nights.", machine: "coin-op-pool" },
];

export const Showroom = ({ index = "03" }) => {
  const [active, setActive] = useState(0);
  const { openRequest } = useMachines();
  const it = ITEMS[active];
  return (
    <section className="relative overflow-hidden border-t border-white/5 py-28 lg:py-36" data-testid="showroom-section">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/[0.07] blur-[140px]" />
      <div className="container-x relative">
        <SectionHead index={index} eyebrow="3D Showroom" title={["Walk the floor", "before we install."]}>
          Spin, drag and explore a few of the machines Brewer places. Pick a model to put it on the turntable.
        </SectionHead>
        <div className="mt-14 grid items-center gap-10 lg:grid-cols-12">
          <div className="order-2 lg:order-1 lg:col-span-4">
            <div className="space-y-2">
              {ITEMS.map((m, i) => (
                <button key={m.name} onClick={() => setActive(i)} className={cn("group relative w-full overflow-hidden rounded-2xl border px-5 py-4 text-left transition-[border-color,background-color] duration-500", active === i ? "border-volt/50 bg-volt/[0.06]" : "border-white/5 hover:border-white/20")} data-testid={`showroom-tab-${i + 1}`}>
                  {active === i && <motion.span layoutId="showroom-bar" className="absolute inset-y-0 left-0 w-[3px] bg-volt" transition={{ duration: 0.5, ease: EASE }} />}
                  <span className="flex items-center justify-between">
                    <span className={cn("font-display text-xl font-semibold transition-colors", active === i ? "text-white" : "text-slate-500 group-hover:text-slate-300")}>{m.name}</span>
                    <span className="font-mono text-[10px] text-slate-600">0{i + 1}</span>
                  </span>
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5, ease: EASE }} className="mt-8" data-testid="showroom-details">
                <p className="eyebrow">{it.tag}</p>
                <p className="mt-3 text-base leading-relaxed text-slate-400">{it.desc}</p>
                <button onClick={() => openRequest(MACHINES.find((x) => x.id === it.machine))} className="btn-primary mt-7 h-12 px-6 text-sm" data-testid="showroom-request-button">
                  Request This Machine <ArrowRight className="btn-arrow h-4 w-4" />
                </button>

              </motion.div>
            </AnimatePresence>
          </div>
          <div className="relative order-1 h-[440px] sm:h-[560px] lg:order-2 lg:col-span-8 lg:h-[640px]">
            <div className="absolute inset-0 rounded-[32px] border border-white/5 bg-gradient-to-b from-ink-800/40 to-transparent" />
            <Boundary>
              <Suspense fallback={null}>
                <Showroom3D active={active} />
              </Suspense>
            </Boundary>
            <div className="pointer-events-none absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-ink-900/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 backdrop-blur-xl">
              <Move3d className="h-3.5 w-3.5 text-volt" /> Drag to explore
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
